import {Component, Input, OnDestroy, OnInit, signal} from '@angular/core';
import {DetailsConfig, SharedUiDetailsModule} from '@snelstart/ui-details';
import {
  AddressModel,
  AdminEntity,
  BokkzAlgDataService,
  BokkzAlgLogic,
  BokkzClient,
  ClientComponent,
  ClientEntity,
  ClientEntityMachtigingStatus,
  ClientEntityMachtigingStatusOb,
  ClientEntityMachtigingStatusTsg,
  CustomerClient,
  CustomerQueryClient,
  CustomerRequest,
  CustomerResponse,
  DossierItemEntity,
  FiscalIdentifierType,
  IntermediaryEntity,
} from '@snelstart-ventures/fiscaal/domain-logic';
import {environment} from '../../../../../../../apps/shells/suite-shell/src/environments/environment';
import {FormBuilder, FormGroup} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {filter, forkJoin, map, Subject, switchMap, takeUntil} from 'rxjs';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiPanelModule} from '@snelstart/ui-panel';
import {SharedUiBadgeModule} from '@snelstart/ui-badge';
import {CommonModule} from '@angular/common';
import {SharedUiCardModule} from '@snelstart/ui-card';
import {ToastService} from '@snelstart/ui-toast';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {dispatchSavedItemEvent} from '@snelstart/domain-logic';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-tax-client-details',
  templateUrl: './feature-tax-client-details.component.html',
  styleUrls: ['./feature-tax-client-details.component.scss'],
  imports: [
    SharedUiDetailsModule,
    SharedUiButtonModule,
    SharedUiPanelModule,
    SharedUiBadgeModule,
    ClientComponent,
    CommonModule,
    SharedUiCardModule,
    SharedUiAlertModule,
  ],
})
export class FeatureTaxClientDetailsComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private destroy$ = new Subject<void>();
  public intermediairyEntity = {} as IntermediaryEntity;
  public dialogClientSettingsVisible = false;
  public clientFormGroup: FormGroup;
  public clientId: any;
  public customerResponse = {} as CustomerResponse;
  public clientEntity = {} as ClientEntity;
  public isLoading = true;

  public showTaxIdAlert = signal(false);

  public detailsConfig = DetailsConfig.create({
    hideArrowButtons: false,
  });

  constructor(
    public fb: FormBuilder,
    private bokkzAlgData: BokkzAlgDataService,
    private algLogic: BokkzAlgLogic,
    public toastService: ToastService,
    private bokkzClient: BokkzClient,
    private customerQueryClient: CustomerQueryClient,
    private customerClient: CustomerClient,
    private activatedRoute: ActivatedRoute,
  ) {
    this.clientFormGroup = this.fb.group({
      id: null as unknown as number,
      sstRelationId: null as unknown as string,
      activeMachtiging: false,
      deleted: false,
      taxId: null as unknown as string,
      taxIdVat: null as unknown as string,
      taxIdWageTax: null as unknown as string,
      adminEntities: null as unknown as AdminEntity[],
      machtigingStatus: null as unknown as ClientEntityMachtigingStatus,
      machtigingStatusOb: null as unknown as ClientEntityMachtigingStatusOb,
      machtigingStatusTsg: null as unknown as ClientEntityMachtigingStatusTsg,
      machtigingStatusToelichting: null as unknown as string,
      typeClient: null as unknown as string,
      rechtsvorm: null as unknown as string,
      aanspreekTitel: null as unknown as string,
      voorletters: null as unknown as string,
      tussenvoegsel: null as unknown as string,
      achternaam: null as unknown as string,
      roepnaam: null as unknown as string,
      name: null as unknown as string,
      postponement: null as unknown as boolean,
      incomeTax: false,
      valueAddedTax: false,
      corporateTax: false,
      wageTax: false,
      kvkId: null as unknown as string,
      classificatie: null as unknown as string,
      sbiCode: null as unknown as string,
      userIdGemachtigde: 0,
      dossierItem: null as unknown as DossierItemEntity,
      deadline: null as unknown as Date,
      street: null as unknown as string,
      houseNumber: null as unknown as number,
      numberAddition: null as unknown as string,
      postalCode: null as unknown as string,
      place: null as unknown as string,
      phone: null as unknown as string,
      mobile: null as unknown as string,
      email: null as unknown as string,
      website: null as unknown as string,
    });
  }

  ngOnInit(): void {
    this.activatedRoute.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.clientId = params['id'];
      if (this.clientId) {
        this.clientFormGroup.reset();
        this.makeCalls(this.clientId);
      }
    });
    this.bokkzAlgData.intermediary.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.intermediairyEntity = value;
    });
    setTimeout(() => {
      this.showTaxIdAlert.set(this.clientEntity.taxId === undefined);
    }, 500);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clientFormGroup.reset();
  }

  onClientChanged(updatedClient: ClientEntity) {
    this.resetClientFormGroup();
    this.bokkzClient
      .postClient(updatedClient)
      .pipe(takeUntil(this.destroy$))
      .subscribe((clientEntityResponse) => {
        this.clientEntity = clientEntityResponse;
      });
    this.bokkzAlgData.updateClient(updatedClient);
    this.clientFormGroup.patchValue(updatedClient, {emitEvent: false});
  }

  private makeCalls(clientId: number) {
    this.bokkzAlgData.intermediary
      .pipe(
        takeUntil(this.destroy$),
        filter((intermediary) => !!intermediary?.sstPrimaryAdminId),
        switchMap((intermediary) =>
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          this.algLogic.verrijkToken(intermediary.sstPrimaryAdminId!).pipe(
            switchMap(() =>
              forkJoin([this.customerQueryClient.getCustomer(this.clientId), this.bokkzClient.getClient(String(clientId))]),
            ),
            map(([customer, clientEntity]) => ({customer, clientEntity})),
          ),
        ),
      )
      .subscribe({
        next: ({customer, clientEntity}) => {
          if (customer && clientEntity == null) {
            const nieuw = {
              email: customer.emailAddressLine?.at(0)!,
              name: customer.name,
              kvkId: customer.kvk,
              phone: customer.phoneNumber,
              mobile: customer.mobileNumber,
              website: customer.website,
              street: customer.address?.streetName,
              houseNumber: customer.address?.streetNumber,
              numberAddition: customer.address?.affix,
              postalCode: customer.address?.postalCode,
              place: customer.address?.city,
              taxId: customer.fiscalIdentifier,
              sstRelationId: customer.id,
              sstPrimaryAdminId: this.intermediairyEntity.sstPrimaryAdminId,
              sstCode: customer.code,
              // sbiCode: customer.sb
            };
            this.bokkzClient
              .postClient(nieuw)
              .pipe(takeUntil(this.destroy$))
              .subscribe((clientEntityResponse) => {
                this.bokkzAlgData.updateClient(clientEntityResponse);
                this.clientEntity = clientEntityResponse;
                this.resetClientFormGroup();
                this.clientFormGroup.patchValue(clientEntityResponse, {emitEvent: false});
              });
          }
          if (customer && clientEntity) {
            this.clientEntity = clientEntity;
            this.customerResponse = customer;

            const merged = {
              ...clientEntity,
              email: customer.emailAddressLine?.at(0)! || clientEntity.email,
              name: customer.name,
              kvkId: customer.kvk || clientEntity.kvkId,
              phone: customer.phoneNumber || clientEntity.mobile,
              mobile: customer.mobileNumber || clientEntity.mobile,
              website: customer.website,
              street: customer.address?.streetName || clientEntity.street,
              houseNumber: customer.address?.streetNumber || clientEntity.houseNumber,
              numberAddition: customer.address?.affix || clientEntity.numberAddition,
              postalCode: customer.address?.postalCode || clientEntity.postalCode,
              place: customer.address?.city || clientEntity.place,
              taxId: customer.fiscalIdentifier ? customer.fiscalIdentifier : clientEntity.taxId,
              // sbiCode: customer.sb
            };

            const updatedCustomerRequest = this.buildUpdatedCustomerRequest(customer, clientEntity);

            this.bokkzClient
              .postClient(merged)
              .pipe(takeUntil(this.destroy$))
              .subscribe((clientEntityResponse) => {
                this.bokkzAlgData.updateClient(clientEntityResponse);
                this.clientEntity = clientEntityResponse;
                this.resetClientFormGroup();
                this.clientFormGroup.patchValue(clientEntityResponse, {emitEvent: false});
              });
            if (updatedCustomerRequest) {
              this.customerClient
                .updateCustomer(updatedCustomerRequest)
                .pipe(takeUntil(this.destroy$))
                .subscribe((result) => {
                  if (result?.id) {
                    dispatchSavedItemEvent({
                      identifier: result.id,
                      groupIdentifier: 'relations',
                      result: {
                        id: result.id,
                        code: result.code,
                        name: result.name,
                        contactPersonName: result.contactName,
                        phoneNumber: result.phoneNumber,
                        mobilePhoneNumber: result.mobileNumber,
                        addressPostalCode: result.address?.postalCode,
                        addressCity: result.address?.city,
                        email: result.emailAddressLine?.length ? result.emailAddressLine[0] : undefined,
                      },
                    });
                  }
                });
            }
          }
        },
        error: (err) => console.error('Error in makeCalls:', err),
        complete: () => {
          this.isLoading = false;
          console.log('complete');
        },
      });
  }

  public showDialogClientSettings() {
    this.dialogClientSettingsVisible = true;
  }

  public openTaxAppInNewTab(taxUrl: string) {
    let url = `${environment.fiscaalBaseUrl}${taxUrl}/${this.clientId}`;
    if (taxUrl.toLowerCase().trim().includes('admin')) {
      const administrationId = this.clientEntity?.adminEntities?.at(0)?.administrationId;
      if (administrationId) {
        let url = `${environment.bookKeepingBaseUrl}${administrationId}`;
        window.open(url, '_blank');
      } else {
        this.toastService.showWarningMessage('Geen administratie.');
      }
    } else {
      window.open(url, '_blank');
    }
  }

  private resetClientFormGroup() {
    this.clientFormGroup = this.fb.group<ClientEntity>({
      id: null as unknown as number,
      sstRelationId: null as unknown as string,
      activeMachtiging: false,
      deleted: false,
      taxId: null as unknown as string,
      taxIdVat: null as unknown as string,
      taxIdWageTax: null as unknown as string,
      adminEntities: null as unknown as AdminEntity[],
      machtigingStatus: null as unknown as ClientEntityMachtigingStatus,
      machtigingStatusOb: null as unknown as ClientEntityMachtigingStatusOb,
      machtigingStatusTsg: null as unknown as ClientEntityMachtigingStatusTsg,
      machtigingStatusToelichting: null as unknown as string,
      typeClient: null as unknown as string,
      rechtsvorm: null as unknown as string,
      aanspreekTitel: null as unknown as string,
      voorletters: null as unknown as string,
      tussenvoegsel: null as unknown as string,
      achternaam: null as unknown as string,
      roepnaam: null as unknown as string,
      name: null as unknown as string,
      postponement: null as unknown as boolean,
      incomeTax: false,
      valueAddedTax: false,
      corporateTax: false,
      wageTax: false,
      kvkId: null as unknown as string,
      classificatie: null as unknown as string,
      sbiCode: null as unknown as string,
      userIdGemachtigde: 0,
      dossierItem: null as unknown as DossierItemEntity,
      deadline: null as unknown as Date,
      street: null as unknown as string,
      houseNumber: null as unknown as number,
      numberAddition: null as unknown as string,
      postalCode: null as unknown as string,
      place: null as unknown as string,
      phone: null as unknown as string,
      mobile: null as unknown as string,
      email: null as unknown as string,
      website: null as unknown as string,
    });
  }

  private buildUpdatedCustomerRequest(customer: CustomerResponse, clientEntity: ClientEntity): CustomerRequest | undefined {
    let updateAddress = false;
    let updatePhone = false;
    let updateMobile = false;
    let updateEmail = false;
    let updateKvk = false;
    let updateFiscaalNummer = false;

    const addressMissing =
      !customer.address?.streetName || !customer.address?.streetNumber || !customer.address?.postalCode || !customer.address?.city;

    if (addressMissing) {
      const street = this.normalizeTextOnly(clientEntity.street);
      const houseNumber = this.normalizeHouseNumber(clientEntity.houseNumber);
      const postalCode = this.normalizePostalCode(clientEntity.postalCode);
      const city = this.normalizeTextOnly(clientEntity.place);

      if (street && houseNumber !== undefined && postalCode && city) {
        updateAddress = true;
      }
    }
    if (!customer.fiscalIdentifier && clientEntity.taxId) updateFiscaalNummer = true;
    if (!customer.phoneNumber && clientEntity.phone) updatePhone = true;
    if (!customer.mobileNumber && clientEntity.mobile) updateMobile = true;

    if ((!customer.emailAddressLine || customer.emailAddressLine.length === 0) && clientEntity.email) {
      updateEmail = true;
    }

    if (!customer.kvk && clientEntity.kvkId) updateKvk = true;

    if (!updateAddress && !updatePhone && !updateMobile && !updateEmail && !updateKvk && !updateFiscaalNummer) return undefined;

    const requestData = new CustomerRequest({
      ...customer,
    });
    // This property (customerGroup) is specifically removed as it's deprecated and will result in a 400 Bad Request if included
    delete requestData.customerGroup;

    if (updateAddress) {
      requestData.address = new AddressModel({
        streetName: this.normalizeTextOnly(clientEntity.street),
        streetNumber: this.normalizeHouseNumber(clientEntity.houseNumber),
        affix: clientEntity.numberAddition,
        postalCode: this.normalizePostalCode(clientEntity.postalCode),
        city: this.normalizeTextOnly(clientEntity.place),
      });
    }

    if (updatePhone) {
      requestData.phoneNumber = clientEntity.phone;
    }

    if (updateMobile) {
      requestData.mobileNumber = clientEntity.mobile;
    }

    if (updateEmail && clientEntity.email) {
      requestData.emailAddressLine = [clientEntity.email];
    }

    if (updateKvk) {
      requestData.kvk = clientEntity.kvkId;
    }

    if (updateFiscaalNummer) {
      requestData.fiscalIdentifier = clientEntity.taxId;
      requestData.fiscalIdentifierType =
        clientEntity.incomeTax || clientEntity.typeClient?.includes('natuurlijk')
          ? FiscalIdentifierType.PersonalIdentity
          : FiscalIdentifierType.LegalEntityAndPartnershipIdentification;
    }

    return requestData;
  }

  normalizePostalCode(pc: string | undefined | null): string | undefined {
    if (!pc) return undefined;

    // Remove spaces and uppercase
    const clean = pc.replace(/\s+/g, '').toUpperCase();

    // Must match 4 digits + 2 letters
    const match = clean.match(/^(\d{4})([A-Z]{2})$/);
    if (!match) return undefined;

    return `${match[1]} ${match[2]}`;
  }

  normalizeHouseNumber(num: string | number | undefined | null): number | undefined {
    if (num == null) return undefined;

    const digits = String(num).replace(/\D+/g, '');

    if (!digits) return undefined;

    const asNumber = Number(digits);

    return Number.isNaN(asNumber) ? undefined : asNumber;
  }

  normalizeTextOnly(text: string | undefined | null): string | undefined {
    if (!text) return undefined;

    // Keep letters and spaces
    const clean = text.replace(/[^a-zA-ZÀ-ÖØ-öø-ÿ\s]/g, '').trim();
    return clean || undefined;
  }
}
